// Projects: форма строки на сетке и запись карточки проекта. Чистые функции.
import type { Change, Row } from '@core/store/model';
import { paletteColorOf } from '@core/palette';
import { BAR_HEIGHT, HEADER_ROW, rowHeight } from '../shared/geometry';
import { RELEASES_LANE } from '../shared/data';

type Sub = { id: string | null; name: string };
export type ProjectDraft = { id: string | null; name: string; color: string; laneIds: string[]; subprojects: Sub[] };

/** Новый проект — в ленту Releases (или первую по порядку, если её нет), чтобы сразу быть на Timeline; цвет — хешем от свежего id. */
export const newProjectDraft = (lanes: Iterable<{ id: string; position: number }> = [], id: string = crypto.randomUUID()): ProjectDraft => {
  const all = [...lanes].sort((a, b) => a.position - b.position);
  const lane = all.find((l) => l.id === RELEASES_LANE) ?? all[0];
  return { id: null, name: '', color: paletteColorOf(id), laneIds: lane ? [lane.id] : [], subprojects: [] };
};
/** Черновик правки: подпроекты в их порядке, ленты Timeline. */
export const projectDraftOf = (
  p: { id: string; name: string; color: string },
  subprojects: Iterable<{ id: string; project_id: string; name: string; position: number }>,
  laneLinks: Iterable<{ lane_id: string; project_id: string }> = [],
): ProjectDraft => ({
  id: p.id, name: p.name, color: p.color, laneIds: [...laneLinks].filter((x) => x.project_id === p.id).map((x) => x.lane_id),
  subprojects: [...subprojects].filter((s) => s.project_id === p.id).sort((a, b) => a.position - b.position).map((s) => ({ id: s.id, name: s.name })),
});

type Project = Pick<Row<'project'>, 'id' | 'name' | 'color' | 'updated_at'>;
type Subproject = Pick<Row<'subproject'>, 'id' | 'project_id' | 'name' | 'position' | 'updated_at'>;

/**
 * Высота и треки строки проекта или подпроекта. Пустой слот снизу — только под курсором с задачей
 * или под контуром новой задачи на не занятом треке. Без видимых задач строка — одна подпись:
 * пустой подпроект под курсором или контуром вырастает; проект с подпроектами — заголовок, в него
 * задачи не заводятся, и он не растёт. Пустая строка не растёт в режиме полных названий.
 */
export function projectRowShape(p: { isSub: boolean; taskCount: number; slots: number[]; isOver: boolean; hintTrack: number | null; withSubs: boolean; barH: number }): { tracks: number; headerOnly: boolean; height: number } {
  const hinted = p.hintTrack !== null;
  const extra = p.isOver || (hinted && !p.slots.includes(p.hintTrack!)) ? 1 : 0;
  const tracks = Math.max(1, p.slots.length + extra);
  const headerOnly = p.taskCount === 0 && (p.isSub ? !p.isOver && !hinted : p.withSubs);
  return { tracks, headerOnly, height: headerOnly ? HEADER_ROW : rowHeight(tracks, p.taskCount ? p.barH : BAR_HEIGHT) };
}

/**
 * Запись карточки проекта: имя, подпроекты разницей (новые — вставка, ушедшие — удаление,
 * переименованные или переставленные — правка). Ошибка валидации возвращается текстом.
 */
type LaneLink = { id: string; lane_id: string; project_id: string };

export function planProjectSave(d: ProjectDraft, projects: Iterable<Project>, subprojects: Iterable<Subproject>, newId: () => string, laneLinks: Iterable<LaneLink> = []): { error: string } | { projectId: string; changes: Change[] } {
  const name = d.name.trim();
  if (!name) return { error: 'Name is required' };
  const subs = d.subprojects.map((s) => ({ ...s, name: s.name.trim() })).filter((s) => s.name);
  if (new Set(subs.map((s) => s.name)).size !== subs.length) return { error: 'Subproject names must differ' };
  const all = [...projects];
  const live = d.id ? all.find((p) => p.id === d.id) : undefined;
  const changes: Change[] = [];
  const projectId = d.id ?? newId();
  if (!d.id) changes.push({ table: 'project', insert: { id: projectId, name, color: d.color } });
  else if (live && (live.name !== name || live.color !== d.color)) {
    changes.push({ table: 'project', id: d.id, set: { ...(live.name !== name ? { name } : {}), ...(live.color !== d.color ? { color: d.color } : {}) }, expect_updated_at: live.updated_at });
  }
  const existing = d.id ? [...subprojects].filter((s) => s.project_id === d.id) : [];
  const keep = new Set(subs.map((s) => s.id).filter(Boolean));
  for (const s of existing) if (!keep.has(s.id)) changes.push({ table: 'subproject', id: s.id, delete: true });
  subs.forEach((s, position) => {
    if (!s.id) { changes.push({ table: 'subproject', insert: { project_id: projectId, name: s.name, position } }); return; }
    const was = existing.find((x) => x.id === s.id);
    if (was && (was.name !== s.name || was.position !== position)) changes.push({ table: 'subproject', id: s.id, set: { name: s.name, position }, expect_updated_at: was.updated_at });
  });
  // ленты Timeline: связь разницей; у нового проекта — все выбранные
  const mine = d.id ? [...laneLinks].filter((x) => x.project_id === d.id) : [];
  for (const x of mine) if (!d.laneIds.includes(x.lane_id)) changes.push({ table: 'lane_project', id: x.id, delete: true });
  for (const laneId of d.laneIds) if (!mine.some((x) => x.lane_id === laneId)) changes.push({ table: 'lane_project', insert: { lane_id: laneId, project_id: projectId } });
  return { projectId, changes };
}
